import React, { useState, useMemo } from 'react';
import { AlertTriangle, ChevronUp, ChevronDown, Search, Send } from 'lucide-react';
import { fetchAiSmartTable } from '../services/analyticsApi';
import { CURRENT_DATA_SCOPE } from '../branding';

// --- Reusable, Plug-and-Play Data Table Component ---
// This component is now self-contained and receives its data and functions via props.
const InteractiveDataTable = ({
    title,
    description,
    data,
    notice,
    onQuerySubmit,
    isLoading,
    query,
    setQuery,
    inputPlaceholder
}) => {
    // State for the global filter/search term within the table
    const [filter, setFilter] = useState('');
    // State to manage sorting configuration
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'ascending' });

    // --- Event Handler for the Input Form ---
    const handleQuerySubmit = (e) => {
        e.preventDefault();
        if (!query.trim()) return;
        onQuerySubmit(query);
    };

    // --- Sorting Logic ---
    const sortedData = useMemo(() => {
        let sortableItems = [...data];
        if (sortConfig.key !== null) {
            sortableItems.sort((a, b) => {
                const valA = a[sortConfig.key];
                const valB = b[sortConfig.key];

                if (valA === null || valA === undefined) return 1;
                if (valB === null || valB === undefined) return -1;

                if (valA < valB) {
                    return sortConfig.direction === 'ascending' ? -1 : 1;
                }
                if (valA > valB) {
                    return sortConfig.direction === 'ascending' ? 1 : -1;
                }
                return 0;
            });
        }
        return sortableItems;
    }, [data, sortConfig]);

    // --- Filtering Logic ---
    const filteredData = useMemo(() => {
        if (!filter) {
            return sortedData;
        }
        return sortedData.filter(item =>
            Object.values(item).some(value =>
                String(value).toLowerCase().includes(filter.toLowerCase())
            )
        );
    }, [sortedData, filter]);

    // --- Function to request a sort on a column ---
    const requestSort = (key) => {
        let direction = 'ascending';
        if (sortConfig.key === key && sortConfig.direction === 'ascending') {
            direction = 'descending';
        }
        setSortConfig({ key, direction });
    };

    // --- Dynamic Headers ---
    const headers = data.length > 0 ? Object.keys(data[0]) : [];

    // --- Render Helper for Sort Icons ---
    const getSortIcon = (key) => {
        if (sortConfig.key !== key) {
            return <span className="opacity-20">↑↓</span>;
        }
        if (sortConfig.direction === 'ascending') {
            return <ChevronUp className="inline-block ml-1 h-4 w-4" />;
        }
        return <ChevronDown className="inline-block ml-1 h-4 w-4" />;
    };

    return (
        <div className="bg-white rounded-lg shadow-md border border-line overflow-hidden">
            {/* --- Data Table Section --- */}
            {data.length > 0 ? (
                <>
                    {/* --- Partial-result notice (set by the API when the row cap cut the result short) --- */}
                    {notice && (
                        <div role="status" className="flex items-start gap-3 p-4 border-b border-amber-200 bg-amber-50 text-amber-900 text-sm rounded-t-lg">
                            <AlertTriangle className="h-5 w-5 flex-shrink-0 text-amber-600" aria-hidden="true" />
                            <div>
                                <p className="font-semibold">Partial result</p>
                                <p>{notice}</p>
                                <p className="mt-1 text-amber-800">Sorting and filtering apply only to the rows shown.</p>
                            </div>
                        </div>
                    )}

                    {/* --- Filter Input --- */}
                    <div className="p-4 border-b border-line">
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 h-5 w-5" />
                            <input
                                type="text"
                                value={filter}
                                onChange={(e) => setFilter(e.target.value)}
                                placeholder="Filter results..."
                                className="w-full bg-background border border-line rounded-md p-2 pl-10 focus:ring-2 focus:ring-accent focus:border-accent focus:outline-none transition-all duration-300"
                            />
                        </div>
                    </div>

                    {/* --- Table: scrolls inside its own box, so the horizontal scrollbar and the header stay visible --- */}
                    <div className="overflow-auto max-h-[70vh]">
                        <table className="min-w-full text-sm text-left text-text-main">
                            <thead className="sticky top-0 z-10 bg-background text-xs text-text-muted uppercase tracking-wider shadow-[inset_0_-1px_0_theme(colors.line)]">
                                <tr>
                                    {headers.map((header) => (
                                        <th key={header} scope="col" className="px-4 py-3 whitespace-nowrap font-semibold">
                                            <div
                                                className="flex items-center cursor-pointer select-none group"
                                                onClick={() => requestSort(header)}
                                            >
                                                {header.replace(/_/g, ' ')}
                                                <span className="ml-2 text-text-muted group-hover:text-primary transition-colors">
                                                    {getSortIcon(header)}
                                                </span>
                                            </div>
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {filteredData.map((row, index) => (
                                    <tr key={row.VIN_CD || index} className="bg-white border-b border-line hover:bg-background transition-colors duration-200">
                                        {headers.map((header) => (
                                            <td key={`${row.VIN_CD}-${header}`} className="px-4 py-3 whitespace-nowrap">
                                                {String(row[header])}
                                            </td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    {/* --- No Results Message --- */}
                    {filteredData.length === 0 && (
                        <div className="text-center py-10 text-gray-500">
                            <p className="text-lg">No results found for "{filter}"</p>
                            <p className="text-sm">Try adjusting your filter.</p>
                        </div>
                    )}
                </>
            ) : (
                /* --- Initial State / No Data Message --- */
                <div className="text-center py-20 px-4 text-gray-500">
                    <div className="inline-block bg-gray-100 p-5 rounded-full border border-gray-200 mb-4">
                        <Search className="h-10 w-10 text-gray-400" />
                    </div>
                    <h2 className="text-lg font-medium text-gray-700">No Data to Display</h2>
                    <p className="mt-1">Enter a query above to fetch and display data.</p>
                </div>
            )}
        </div>
    );
};


// --- Main App Component (Demonstration of how to use the component) ---
export default function SmartTable() {
    // State for the user's input query
    const [query, setQuery] = useState('');
    // State to hold the data from the API
    const [data, setData] = useState([]);
    // Note returned with partial results (null when the result is complete)
    const [notice, setNotice] = useState(null);
    // State to manage loading status
    const [isLoading, setIsLoading] = useState(false);

    const handleApiSubmit = async (userQuery) => {
        console.log(`Sending query to API: "${userQuery}"`);
        setIsLoading(true);
        setData([]); // Clear previous data
        setNotice(null);

        try {
            const response = await fetchAiSmartTable(userQuery);

            if (!response.ok) {
                throw new Error("API Error");
            }
            

            const json = await response.json();

            console.log("API Response:", json);
            if(json.type == "table"){
                setData(json.content);
                setNotice(json.notice || null);
            }else{
                alert(json.content); // a text answer (e.g. "not available"), not a table
            }

            // Assuming json.content is the table-compatible array
            
        } catch (error) {
            console.error("Error fetching from API:", error);
        } finally {
            setIsLoading(false);
        }
    };

    // --- Event Handler for the Input Form ---
    const handleQuerySubmit = (e) => {
        e.preventDefault();
        if (!query.trim()) return; // Don't submit if query is empty
        handleApiSubmit(query);
    };

    return (
        <div className="bg-background text-text-main min-h-screen font-sans p-4 sm:p-6 lg:p-8">
            <div className="max-w-8xl mx-auto space-y-8">
                {/* --- Input Section --- */}
                <div className="bg-white rounded-lg p-6 shadow-md border border-line">
                    <h1 className="text-xl sm:text-2xl font-semibold text-primary mb-1">Data Query Interface</h1>
                    <p className="text-text-muted mb-4">Enter your query to fetch and analyze warranty claim data. {CURRENT_DATA_SCOPE}</p>
                    <form onSubmit={handleQuerySubmit} className="flex items-center gap-3">
                        <input
                            type="text"
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            placeholder="e.g., 'Show all claims for the last week'"
                            className="flex-grow bg-background border border-line rounded-md p-3 focus:ring-2 focus:ring-accent focus:border-accent focus:outline-none transition-all duration-300 placeholder-text-muted"
                        />
                        <button
                            type="submit"
                            disabled={isLoading}
                            className="bg-primary hover:bg-primary-dark disabled:bg-line-strong text-white font-bold py-3 px-5 rounded-md flex items-center justify-center transition-all duration-300"
                        >
                            {isLoading ? (
                                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                            ) : (
                                <Send className="h-5 w-5" />
                            )}
                        </button>
                    </form>
                </div>

                {/* --- Reusable Component Usage --- */}
                <InteractiveDataTable
                    data={data}
                    notice={notice}
                    isLoading={isLoading}
                />
            </div>
        </div>
    );
}
